import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Brain, Flag, WarningCircle, CaretRight } from "@phosphor-icons/react";
import { AdminHeader } from "@/components/admin/parts";
import { adminApi } from "@/lib/api";

export default function Feedback() {
  const [flagged, setFlagged] = useState([]);
  const [low, setLow] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([
      adminApi.get("/admin/conversations", { params: { flagged: true } }),
      adminApi.get("/admin/conversations", { params: { low: true } }),
    ])
      .then(([f, l]) => { setFlagged(f.data); setLow(l.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div>
      <AdminHeader title="AI Feedback" subtitle="Review flagged answers and low-confidence responses to improve Nick" />
      <div className="p-8 space-y-8">
        <div className="bg-gradient-to-r from-[#1a1a12] to-[#0F0F0F] border border-[#73bf44]/20 rounded-xl p-6 flex items-start gap-4">
          <Brain size={28} weight="duotone" className="text-[#73bf44] shrink-0" />
          <div>
            <h2 className="font-heading font-semibold text-white">How Nick learns</h2>
            <p className="text-sm text-neutral-400 mt-1 max-w-2xl">
              Open any conversation and use <span className="text-[#73bf44]">Review answer</span> to mark responses correct or
              incorrect and supply the accurate technical answer. Approved corrections become knowledge-base entries that Nick
              uses in future conversations — nothing is learned automatically without your approval.
            </p>
          </div>
        </div>

        <Section title="Flagged Answers" icon={Flag} rows={flagged} empty="No flagged conversations." navigate={navigate} loading={loading} />
        <Section title="Low-Confidence Responses" icon={WarningCircle} rows={low} empty="No low-confidence conversations." navigate={navigate} loading={loading} />
      </div>
    </div>
  );
}

function Section({ title, icon: Icon, rows, empty, navigate, loading }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <Icon size={18} className="text-amber-400" />
        <h3 className="font-heading font-semibold text-white">{title}</h3>
        <span className="text-xs text-neutral-600">({rows.length})</span>
      </div>
      {loading ? (
        <div className="text-neutral-600 text-sm">Loading...</div>
      ) : rows.length === 0 ? (
        <div className="text-sm text-neutral-600 border border-dashed border-neutral-800 rounded-xl py-8 text-center">{empty}</div>
      ) : (
        <div className="bg-[#0F0F0F] border border-neutral-800 rounded-xl divide-y divide-neutral-800/60">
          {rows.map((c) => (
            <button
              key={c.id}
              onClick={() => navigate(`/admin/conversations/${c.id}`)}
              data-testid="feedback-conversation-row"
              className="w-full flex items-center gap-3 px-5 py-3.5 text-left hover:bg-white/[0.02] transition-colors group"
            >
              <div className="flex-1 min-w-0">
                <div className="text-sm text-neutral-200 truncate">{c.last_message || "New conversation"}</div>
                <div className="text-xs text-neutral-600">{c.chassis_number ? `${c.model || ""} · ${c.chassis_number}` : "No caravan linked"}</div>
              </div>
              <CaretRight size={16} className="text-neutral-600 group-hover:text-white" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
